import type { VideoStateManager } from '.';
import type { CallableToolArgsMap } from './callable-tools';

export type ExecuteToolFn = <T extends keyof CallableToolArgsMap>(
    toolName: T,
    params: CallableToolArgsMap[T],
    assistantMessage?: string,
) => Promise<void>;

export interface Usage {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
    latency?: number; // in milliseconds
    // Anthropic prompt-caching counters, separate from prompt_tokens (which
    // stays the uncached remainder). Undefined for non-Anthropic models.
    cache_creation_input_tokens?: number;
    cache_read_input_tokens?: number;
}

export interface ModelOption {
    value: Model;
    label: string;
}
export enum Model {
    GPT_4_1_MINI = 'gpt-4.1-mini-2025-04-14',
    GPT_4_1_NANO = 'gpt-4.1-nano-2025-04-14',
    GPT_4_1 = 'gpt-4.1',
    GPT_4O = 'gpt-4o',
    GPT_4O_MINI = 'gpt-4o-mini',
    GPT_OSS_20B = 'openai/gpt-oss-20b',
    GPT_OSS_120B = 'openai/gpt-oss-120b',
    GPT_5_5 = 'gpt-5.5-2026-04-23',
    GPT_5_4 = 'gpt-5.4-2026-03-05',
    GPT_5_4_MINI = 'gpt-5.4-mini-2026-03-17',
    GPT_5_2 = 'gpt-5.2-2025-12-11',
    GPT_5_1 = 'gpt-5.1-2025-11-13',
    GPT_5 = 'gpt-5-2025-08-07',
    GPT_5_MINI = 'gpt-5-mini-2025-08-07',
    GPT_5_NANO = 'gpt-5-nano-2025-08-07',
    GEMINI_2_5_FLASH = 'google/gemini-2.5-flash',
    GEMINI_2_5_PRO = 'google/gemini-2.5-pro',
    GEMINI_3_FLASH_PREVIEW = 'google/gemini-3-flash-preview',
    GEMINI_3_5_FLASH = 'google/gemini-3.5-flash',
    CLAUDE_FABLE_5 = 'claude-fable-5@default',
    CLAUDE_OPUS_4_8 = 'claude-opus-4-8@default',
    CLAUDE_SONNET_4_6 = 'claude-sonnet-4-6@default',
    CLAUDE_HAIKU_4_5 = 'claude-haiku-4-5@20251001',
}

export const AVAILABLE_MODELS: ModelOption[] = [
    // GPT 5.x family (newest)
    {
        value: Model.GPT_5_5,
        label: 'GPT 5.5',
    },
    {
        value: Model.GPT_5_4,
        label: 'GPT 5.4',
    },
    {
        value: Model.GPT_5_4_MINI,
        label: 'GPT 5.4 mini',
    },
    {
        value: Model.GPT_5_2,
        label: 'GPT 5.2',
    },
    {
        value: Model.GPT_5_1,
        label: 'GPT 5.1',
    },
    {
        value: Model.GPT_5,
        label: 'GPT 5',
    },
    {
        value: Model.GPT_5_MINI,
        label: 'GPT 5 mini',
    },
    {
        value: Model.GPT_5_NANO,
        label: 'GPT 5 nano',
    },
    // GPT 4.x family
    {
        value: Model.GPT_4_1,
        label: 'GPT 4.1',
    },
    {
        value: Model.GPT_4_1_MINI,
        label: 'GPT 4.1 mini',
    },
    {
        value: Model.GPT_4_1_NANO,
        label: 'GPT 4.1 nano',
    },
    {
        value: Model.GPT_4O,
        label: 'GPT 4o',
    },
    {
        value: Model.GPT_4O_MINI,
        label: 'GPT 4o mini',
    },
    // Open source models
    {
        value: Model.GPT_OSS_120B,
        label: 'GPT OSS 120B',
    },
    {
        value: Model.GPT_OSS_20B,
        label: 'GPT OSS 20B',
    },
    // Gemini models
    {
        value: Model.GEMINI_3_5_FLASH,
        label: 'Gemini 3.5 Flash',
    },
    {
        value: Model.GEMINI_3_FLASH_PREVIEW,
        label: 'Gemini 3 Flash',
    },
    {
        value: Model.GEMINI_2_5_PRO,
        label: 'Gemini 2.5 Pro',
    },
    {
        value: Model.GEMINI_2_5_FLASH,
        label: 'Gemini 2.5 Flash',
    },
    // Claude models
    {
        value: Model.CLAUDE_FABLE_5,
        label: 'Claude Fable 5',
    },
    {
        value: Model.CLAUDE_OPUS_4_8,
        label: 'Claude Opus 4.8',
    },
    {
        value: Model.CLAUDE_SONNET_4_6,
        label: 'Claude Sonnet 4.6',
    },
];

export const DEFAULT_CHAT_MODEL: ModelOption =
    AVAILABLE_MODELS.find((m) => m.value === Model.GPT_4_1_MINI) || AVAILABLE_MODELS[0];

export const AVAILABLE_CODEO_MODELS: ModelOption[] = [
    { value: Model.CLAUDE_FABLE_5, label: 'Claude Fable 5' },
    { value: Model.CLAUDE_OPUS_4_8, label: 'Claude Opus 4.8' },
    { value: Model.CLAUDE_SONNET_4_6, label: 'Claude Sonnet 4.6' },
    { value: Model.CLAUDE_HAIKU_4_5, label: 'Claude Haiku 4.5' },
    { value: Model.GPT_5_5, label: 'GPT 5.5' },
    { value: Model.GPT_5_4, label: 'GPT 5.4' },
    { value: Model.GPT_5_4_MINI, label: 'GPT 5.4 mini' },
    { value: Model.GEMINI_3_5_FLASH, label: 'Gemini 3.5 Flash' },
    { value: Model.GEMINI_3_FLASH_PREVIEW, label: 'Gemini 3 Flash' },
    { value: Model.GEMINI_2_5_PRO, label: 'Gemini 2.5 Pro' },
];

export const DEFAULT_CODEO_MODEL: ModelOption =
    AVAILABLE_CODEO_MODELS.find((m) => m.value === Model.CLAUDE_SONNET_4_6) ||
    AVAILABLE_CODEO_MODELS[0];

// Model knowledge cutoff dates (based on OpenAI documentation)
// Reference: https://platform.openai.com/docs/models
export const MODEL_KNOWLEDGE_CUTOFFS: Record<Model, string> & { default: string } = {
    [Model.GPT_4_1_MINI]: 'June 01, 2024',
    [Model.GPT_4_1_NANO]: 'June 01, 2024',
    [Model.GPT_4_1]: 'June 01, 2024',
    [Model.GPT_4O]: 'October 01, 2023',
    [Model.GPT_4O_MINI]: 'October 01, 2023',
    [Model.GPT_5_5]: 'December 01, 2025',
    [Model.GPT_5_4]: 'August 31, 2025',
    [Model.GPT_5_4_MINI]: 'August 31, 2025',
    [Model.GPT_5_2]: 'August 31, 2025',
    [Model.GPT_5_1]: 'September 30, 2024',
    [Model.GPT_5]: 'September 30, 2024',
    [Model.GPT_5_MINI]: 'May 31, 2024',
    [Model.GPT_5_NANO]: 'May 31, 2024',
    [Model.GPT_OSS_20B]: 'June 01, 2024',
    [Model.GPT_OSS_120B]: 'June 01, 2024',
    [Model.GEMINI_2_5_FLASH]: 'January 01, 2025',
    [Model.GEMINI_2_5_PRO]: 'January 01, 2025',
    [Model.GEMINI_3_FLASH_PREVIEW]: 'January 01, 2025',
    [Model.GEMINI_3_5_FLASH]: 'January 01, 2025',
    [Model.CLAUDE_FABLE_5]: 'January 01, 2026',
    [Model.CLAUDE_OPUS_4_8]: 'January 01, 2026',
    [Model.CLAUDE_SONNET_4_6]: 'March 01, 2025',
    [Model.CLAUDE_HAIKU_4_5]: 'March 01, 2025',
    // Default for unknown models
    default: 'Unknown',
};

/**
 * Get the knowledge cutoff date for a given model
 */
export function getKnowledgeCutoff(modelId: string): string {
    return MODEL_KNOWLEDGE_CUTOFFS[modelId as Model] || MODEL_KNOWLEDGE_CUTOFFS['default'];
}

// Model context window limits (safe limits at ~80% of actual context windows)
// Used for automatic conversation truncation to prevent context_length_exceeded errors
export const MODEL_CONTEXT_LIMITS: Record<string, number> = {
    // GPT-4.x family (128K context)
    [Model.GPT_4_1_MINI]: 100_000,
    [Model.GPT_4_1_NANO]: 100_000,
    [Model.GPT_4_1]: 100_000,
    [Model.GPT_4O]: 100_000,
    [Model.GPT_4O_MINI]: 100_000,
    // GPT-5/5-mini/5-nano (128K context)
    [Model.GPT_5]: 100_000,
    [Model.GPT_5_MINI]: 100_000,
    [Model.GPT_5_NANO]: 100_000,
    [Model.GPT_5_1]: 100_000,
    // GPT-5.2+ family (200K context)
    [Model.GPT_5_2]: 160_000,
    [Model.GPT_5_4]: 160_000,
    [Model.GPT_5_4_MINI]: 160_000,
    // GPT-5.5 (1M+ context)
    [Model.GPT_5_5]: 800_000,
    // GPT-OSS models
    [Model.GPT_OSS_20B]: 100_000,
    [Model.GPT_OSS_120B]: 100_000,
    // Gemini models (1M context)
    [Model.GEMINI_2_5_FLASH]: 800_000,
    [Model.GEMINI_2_5_PRO]: 800_000,
    [Model.GEMINI_3_FLASH_PREVIEW]: 800_000,
    [Model.GEMINI_3_5_FLASH]: 800_000,
    // Claude models. Fable 5 supports a 1M context, but we cap it to match Opus.
    [Model.CLAUDE_FABLE_5]: 160_000,
    [Model.CLAUDE_OPUS_4_8]: 160_000,
    [Model.CLAUDE_SONNET_4_6]: 160_000,
    [Model.CLAUDE_HAIKU_4_5]: 160_000,
};

const DEFAULT_CONTEXT_LIMIT = 100_000;

/**
 * Get the context token limit for a given model
 */
export function getContextLimit(modelId: string): number {
    return MODEL_CONTEXT_LIMITS[modelId] || DEFAULT_CONTEXT_LIMIT;
}

// Models that support Anthropic adaptive thinking (`thinking: {type:'adaptive'}`)
// and its summarized reasoning stream. Opus 4.6+, Sonnet 4.6, and Fable 5 support
// it; Haiku 4.5, older Claude models, and non-Claude models do not.
const ADAPTIVE_THINKING_MODELS: ReadonlySet<string> = new Set([
    Model.CLAUDE_FABLE_5,
    Model.CLAUDE_OPUS_4_8,
    Model.CLAUDE_SONNET_4_6,
]);

/**
 * Whether a model accepts the Anthropic adaptive-thinking parameter. Callers
 * that enable thinking must gate on this so unsupported models (e.g. Haiku 4.5)
 * don't receive a config they would reject.
 */
export function modelSupportsAdaptiveThinking(modelId: string): boolean {
    return ADAPTIVE_THINKING_MODELS.has(modelId);
}

/**
 * Get the current date formatted for display in system prompts
 * Format: "January 10, 2025"
 */
export function getCurrentDate(): string {
    const now = new Date();
    return now.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export interface ToolDefinition {
    type: 'function';
    function: {
        name: string;
        description: string;
        parameters: {
            type: 'object';

            properties: Record<string, any>;
            required?: string[];
        };
    };
}

export interface ToolExecutionContext {
    parentActionGroup: string;
    toolCallId: string;
    conversationId?: string;
    stopTool?: string;
    abortSignal?: AbortSignal;
    abortController?: AbortController;
    task?: string;
    videoStateManager?: VideoStateManager;
}

export enum CustomRendererMode {
    /** Shows custom UI only when tool name is clicked (inside collapse) */
    INLINE_HIDDEN = 'inline_hidden',
    /** Shows tool name + spinner, with custom UI always visible below */
    INLINE = 'inline',
    /** Custom renderer controls the full display (no tool name/spinner) */
    FULL = 'full',
}

export type ToolResult = string | (TextContent | ImageContent)[];

export interface EnhancedToolResult {
    result: string;
    /** Multipart content (text + images) to send as the tool message content to the LLM.
     *  When set, this is used as the tool message content instead of `result`.
     *  `result` is still used for display purposes (custom renderers, logging). */
    resultContent?: (TextContent | ImageContent)[];

    injectAsUserMessage?: any;
    isSuccess?: boolean;
    // When true, the conversation loop stops after this tool without making another LLM call
    stopConversation?: boolean;
}

export enum MessageRole {
    SYSTEM = 'system',
    USER = 'user',
    ASSISTANT = 'assistant',
    TOOL = 'tool',
}

export interface ImageContent {
    type: 'image_url';
    image_url: {
        url: string;
    };
}

export interface TextContent {
    type: 'text';
    text: string;
}

export type MessageContent = string | (TextContent | ImageContent)[];

export interface BaseMessage {
    id?: string;
    role: MessageRole;
    content: MessageContent;
    isContextMessage?: boolean;
    isDisplayOnly?: boolean;
    isVideoStateUpdateMessage?: boolean;
}

export interface SystemMessage extends BaseMessage {
    role: MessageRole.SYSTEM;
    content: string;
}

export const isSystemMessage = (message: ConversationMessage): message is SystemMessage => {
    return message.role === MessageRole.SYSTEM;
};

export interface UserMessage extends BaseMessage {
    role: MessageRole.USER;
    content: MessageContent;
}

export const isUserMessage = (message: ConversationMessage): message is UserMessage => {
    return message.role === MessageRole.USER;
};

export interface CodeInterpreterFile {
    file_id: string;
    filename: string;
    mime_type?: string;
}

export interface CodeInterpreterOutput {
    container_id: string;
    code?: string;
    logs?: string;
    files?: CodeInterpreterFile[];
}

/** A summarized extended-thinking block returned by Anthropic models. */
export interface ThinkingBlock {
    type: 'thinking';
    thinking: string;
    /** Opaque signature that must be replayed unmodified on later turns. */
    signature: string;
}

/** A redacted thinking block (content withheld but must still be replayed). */
export interface RedactedThinkingBlock {
    type: 'redacted_thinking';
    data: string;
}

export type ReasoningBlock = ThinkingBlock | RedactedThinkingBlock;

export interface AssistantMessage extends BaseMessage {
    role: MessageRole.ASSISTANT;
    content: MessageContent;
    tool_calls?: ToolCall[];
    usage?: Usage;
    code_interpreter_outputs?: CodeInterpreterOutput[];
    openai_response_id?: string;
    /**
     * Raw thinking blocks from the assistant turn. Must be replayed unmodified
     * (Anthropic rejects a turn that drops the thinking blocks preceding a
     * tool_use), so these are sent back verbatim by getMessagesForAPI().
     */
    thinking_blocks?: ReasoningBlock[];
    /**
     * Human-readable reasoning summary for display. Updated live while the
     * model thinks; not sent back to the API (replay uses thinking_blocks).
     */
    reasoning?: string;
}

export const isAssistantMessage = (message: ConversationMessage): message is AssistantMessage => {
    return message.role === MessageRole.ASSISTANT;
};

export interface ToolCall {
    id: string;
    type: 'function';
    function: {
        name: string;
        arguments: string;
    };
}

export interface ToolMessage extends BaseMessage {
    role: MessageRole.TOOL;
    content: ToolResult;
    tool_call_id: string;
    /** Multipart content (text + images) sent to the LLM in place of `content`.
     *  When set, `getMessagesForAPI()` uses this instead of `content`.
     *  `content` remains the text-only string for display/custom renderers. */
    apiContent?: (TextContent | ImageContent)[];
    isSuccess?: boolean;
    child_conversation_ids?: string[]; // IDs of child conversations spawned by this tool call
    artifact_task_id?: string; // Task ID for viewing oracle artifact in admin
    usage?: Usage; // tool execution metrics (latency tracked here)
}

export const isToolMessage = (message: ConversationMessage): message is ToolMessage => {
    return message.role === MessageRole.TOOL;
};

export type ConversationMessage = SystemMessage | UserMessage | AssistantMessage | ToolMessage;

export type CustomRendererComponentProps = {
    isLoading: boolean;
    result: string;
    toolCall: ToolCall | null;
    debugDetails?: React.ReactNode;
};
